import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { fetchCompletedSentences } from "@store/thunks/dashboardThunk";
import { getStatusStyle } from "@/utils/constants";

const languageIdToName = {
    1: "Hindi",
    2: "English",
    3: "Bengali",
    4: "Marathi",
    5: "Telugu",
    6: "Tamil",
    7: "Urdu",
    8: "Gujarati",
    9: "Kannada",
    10: "Odia",
    11: "Punjabi",
    12: "Malayalam",
    13: "Assamese",
    14: "Maithili",
    15: "Santali",
    16: "Kashmiri",
    17: "Nepali",
    18: "Sindhi",
    19: "Dogri",
    20: "Konkani",
    21: "Bodo",
    22: "Sanskrit",
    23: "Manipuri",
};

const DevnagriValidatedFilesSentencesTable = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { selectedFileId, selectedTargetId, sentences } = useSelector((store) => store.dashboard);
    const [clickReject, setClickReject] = useState(false);
    const [activeTab, setActiveTab] = useState("all");
    const [loading, setLoading] = useState(false);
    const [allSentences, setAllSentences] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [hasNextPage, setHasNextPage] = useState(true);

    const handleAllSentencesClick = async () => {
        if (activeTab === "all") return; // Do nothing if already on All Sentences tab
        setActiveTab("all");
        setClickReject(false);
        setPageIndex(0);
        setHasNextPage(true);
        setLoading(true);
        if (selectedTargetId && selectedFileId) {
            const res = await dispatch(fetchCompletedSentences("Devnagri", selectedFileId, selectedTargetId, false));
            if (Array.isArray(res?.data)) {
                setAllSentences(res.data);
                setHasNextPage(res.data.length > pageSize);
            } else {
                setAllSentences([]);
                setHasNextPage(false);
            }
        } else {
            navigate("/superAdmin/devnagri");
        }
        setLoading(false);
    };

    const handleRejectedSentencesClick = async () => {
        if (activeTab === "rejected") return; // Do nothing if already on Rejected Sentences tab
        setActiveTab("rejected");
        setClickReject(true);
        setPageIndex(0);
        setHasNextPage(true);
        setLoading(true);
        if (selectedTargetId && selectedFileId) {
            const res = await dispatch(fetchCompletedSentences("Devnagri", selectedFileId, selectedTargetId, true));
            if (Array.isArray(res?.data)) {
                setAllSentences(res.data);
                setHasNextPage(res.data.length > pageSize);
            } else {
                setAllSentences([]);
                setHasNextPage(false);
            }
        } else {
            navigate("/superAdmin/devnagri");
        }
        setLoading(false);
    };

    useEffect(() => {
        if (selectedTargetId && selectedFileId) {
            setLoading(true);
            dispatch(fetchCompletedSentences("Devnagri", selectedFileId, selectedTargetId, clickReject))
                .then((res) => {
                    if (Array.isArray(res?.data)) {
                        setAllSentences(res.data);
                        setHasNextPage(res.data.length > pageSize);
                    } else {
                        setAllSentences([]);
                        setHasNextPage(false);
                    }
                })
                .finally(() => setLoading(false));
        } else {
            navigate("/superAdmin/devnagri");
        }
    }, [dispatch, selectedFileId, selectedTargetId, navigate, clickReject]);

    const paginatedSentences = useMemo(() => {
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        return allSentences.slice(start, end);
    }, [allSentences, pageIndex, pageSize]);


    useEffect(() => {
        setHasNextPage((pageIndex + 1) * pageSize < allSentences.length);
    }, [allSentences, pageIndex, pageSize]);

    const handleClickHome = () => {
        navigate("/superAdmin");
    };

    const handleClickBack = () => {
        navigate(-1);
    };

    const sentenceColumns = [
        {
            header: "Sr. No.",
            cell: ({ row }) => (
                <div className="text-center text-sm font-['Semantic/Strong']">
                    {pageIndex * pageSize + row.index + 1}
                </div>
            ),
            cellClassName: "text-center",
        },
        {
    header: "CDAC Source Text",
    accessorKey: "cdacSentenceText",
    cell: ({ row }) => (
        <div className="whitespace-normal break-words w-full px-4 text-base text-gray-900 font-['Semantic/Strong'] py-2">
            {row.original.cdacSentenceText || "N/A"}
        </div>
    ),
},
{
    header: "Dev Source Text",
    accessorKey: "devSentenceText",
    cell: ({ row }) => (
        <div className="whitespace-normal break-words w-full px-4 text-base text-gray-900 font-['Semantic/Strong'] py-2">
            {row.original.devSentenceText || "N/A"}
        </div>
    ),
},

        !clickReject && {
            header: `GS Validated Text (${languageIdToName[selectedTargetId] || "Unknown"})`,
            accessorKey: "validatedTextGs",
            cell: ({ row }) => (
                <div className="whitespace-normal break-words w-full px-4 text-base text-gray-900 font-['Semantic/Strong'] py-2">
                    {row.original.validatedTextGs || "N/A"}
                </div>
            ),
        },
        !clickReject && {
            header: `Dev Validated Text (${languageIdToName[selectedTargetId] || "Unknown"})`,
            accessorKey: "validatedTextDev",
            cell: ({ row }) => (
                <div className="whitespace-normal break-words w-full px-4 text-base text-gray-900 font-['Semantic/Strong'] py-2">
                    {row.original.validatedTextDev || "N/A"}
                </div>
            ),
        },
        {
            header: "Validation Status",
            accessorKey: "validationStatus",
            cell: ({ row }) => {
                const style = getStatusStyle(row.original.validationStatusDev);
                return (
                    <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
                        {style.label}
                    </div>
                );
            },
        },
        {
            header: "LAPSE Score",
            cell: ({ row }) => (
                <div
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${row.original.lapseScore >= 70
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}
                >
                    {row.original.qualityScorePercentage}%
                </div>

            ),
        },
    ].filter(Boolean);

    return (
        <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50 mt-14">
            <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                        <div className="flex flex-col">
                            <h1 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">
                                <span className="text-[#ec7026] shadow-sm">Devnagri</span> Completed Projects
                            </h1>
                            <p className="text-sm text-gray-500 font-['Semantic/Strong']">
                                View and manage completed Devnagri projects
                            </p>
                        </div>

                        <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
                            <button
                                onClick={handleClickHome}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                            >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                    />
                                </svg>
                                <span>Home</span>
                            </button>

                            <button
                                onClick={handleClickBack}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                            >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                <span>Back</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full p-4 sm:p-6 lg:p-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex items-center mb-4">
                            <div className="flex border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                <button
                                    className={`inline-flex items-center px-4 py-2 text-sm font-['Semantic/Strong'] ${activeTab === "all" ? "bg-[#ec7026] text-white" : "bg-white text-[#ec7026] hover:bg-gray-50"
                                        } transition-colors duration-200`}
                                    onClick={handleAllSentencesClick}
                                >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                        />
                                    </svg>
                                    All Sentences
                                </button>
                                <button
                                    className={`inline-flex items-center px-4 py-2 text-sm font-['Semantic/Strong'] ${activeTab === "rejected" ? "bg-[#ec7026] text-white" : "bg-white text-[#ec7026] hover:bg-gray-50"
                                        } transition-colors duration-200`}
                                    onClick={handleRejectedSentencesClick}
                                >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Rejected Sentences
                                </button>
                            </div>
                        </div>
                        {loading ? (
                            <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                                <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                                <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                                <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                            </div>
                        ) : !selectedFileId || !selectedTargetId ? (
                            <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                                Please select a file and target language to view sentences.
                            </div>
                        ) : allSentences === null || allSentences === undefined ? (
                            <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                                Loading sentences...
                            </div>
                        ) : !Array.isArray(allSentences) ? (
                            <div className="p-6 text-center text-red-500 font-['Semantic/Strong']">
                                Error: Unexpected sentence data format.
                            </div>
                        ) : allSentences.length === 0 ? (
                            <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                                <p>{clickReject ? "No rejected sentences found." : "No approved sentences found."}</p>
                                <p className="mt-2 text-sm">
                                    {clickReject
                                        ? "No rejected sentences available for this file."
                                        : "No approved sentences available for this file."}
                                </p>
                            </div>
                        ) : (
                            <div className="w-full overflow-x-auto">
                                <GenericTable
                                    data={paginatedSentences}
                                    columns={sentenceColumns}
                                    showPagination={true}
                                    manualPagination={true}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    hasNextPage={hasNextPage}
                                    onPaginationChange={({ pageIndex, pageSize }) => {
                                        setPageIndex(pageIndex);
                                        setPageSize(pageSize);
                                    }}
                                    emptyMessage={`No ${clickReject ? "rejected" : "approved"} sentences available for this file`}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DevnagriValidatedFilesSentencesTable;